import { type FormEvent, type ReactElement, useState } from 'react';

import { Button } from '@autop/ui/src/button';

import { useSubmitSurvey } from '@/features/submit-survey';
import {
  SURVEY_CONSULTING_OPTIONS,
  SURVEY_SECTIONS,
  SURVEY_STORY_MAX_LENGTH,
  type SurveyChoiceKey,
} from '@/shared/config';
import { Honeypot } from '@/shared/ui/honeypot';

import {
  countAnswered,
  EMPTY_DRAFT,
  findProblems,
  SURVEY_REQUIRED_COUNT,
  type SurveyDraft,
  type SurveyFieldKey,
  type SurveyProblem,
  toSubmission,
} from '../model/survey-draft';
import { ChoiceQuestion } from './choice-question';
import { QuestionTitle } from './question-title';
import { SurveyConsent } from './survey-consent';
import { SurveySent } from './survey-sent';

const FALLBACK_MESSAGE = '설문을 제출하지 못했습니다. 잠시 후 다시 시도해 주세요';

/** 서버가 준 문구를 그대로 보여준다 (문의하기와 같은 방식). */
const toMessage = (error: unknown): string =>
  error instanceof Error && error.message.length > 0 ? error.message : FALLBACK_MESSAGE;

const inputClass =
  'rounded-md border border-border bg-card px-3 text-sm focus:border-foreground focus:outline-none focus:ring-[3px] focus:ring-primary';

const hintClass = 'text-[12.5px] leading-[1.7] text-muted-foreground';

/**
 * 빠진 문항으로 화면을 옮기고 고칠 칸에 초점을 둔다 — 키보드 사용자도 바로 고칠 수 있게.
 * 「기타」 내용이 빈 경우는 보기가 아니라 그 내용 칸이 고칠 자리다.
 */
const moveTo = (problem: SurveyProblem): void => {
  const question = document.getElementById(`survey-q-${problem.key}`);
  const field =
    problem.target === 'other'
      ? document.getElementById(`survey-${problem.key}-other`)
      : question?.querySelector<HTMLElement>('input, textarea');
  question?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  field?.focus({ preventScroll: true });
};

/** 비어 있지 않은 id 만 이어 붙인다. 하나도 없으면 속성 자체를 빼야 한다. */
const describedBy = (...ids: (string | false)[]): string | undefined =>
  ids.filter(Boolean).join(' ') || undefined;

/** 오류 문구. 입력칸의 aria-describedby 가 이 id 를 가리켜야 화면낭독기가 함께 읽는다. */
function FieldError({ id, message }: { id: string; message: string | undefined }) {
  return message ? (
    <p id={id} className="text-[12.5px] text-destructive">
      {message}
    </p>
  ) : null;
}

const homepageOf = (form: HTMLFormElement): string | undefined => {
  const value = new FormData(form).get('homepage');
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
};

function ProgressBar({ answered }: { answered: number }): ReactElement {
  const percent = Math.round((answered / SURVEY_REQUIRED_COUNT) * 100);
  return (
    <div className="sticky top-16 z-10 -mx-4 flex items-center gap-3 border-border border-b bg-background/90 px-4 py-3 backdrop-blur">
      <div
        role="progressbar"
        aria-label="설문 진행률"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="h-2 flex-1 overflow-hidden rounded-full bg-secondary"
      >
        <div
          className="h-full origin-left rounded-full bg-primary transition-transform duration-300"
          style={{ transform: `scaleX(${percent / 100})` }}
        />
      </div>
      <span data-numeric="" className="w-10 shrink-0 text-right font-mono text-[12px] font-bold">
        {percent}%
      </span>
    </div>
  );
}

/**
 * AI 업무 자동화 수요 조사 설문 — 기존 www.autop.kr/survey 를 옮겨 왔다.
 *
 * 로그인 없이 참여한다. 유튜브를 보고 온 사람 대부분이 비회원이고, 가입을 먼저 요구하면
 * 설문이 거기서 끝난다.
 *
 * 빠진 문항은 **제출할 때 한 번에** 알린다. 한 칸씩 막으면 답을 고르는 흐름이 끊긴다.
 * 첫 제출을 시도한 뒤로는 고치는 즉시 표시가 사라지도록 매번 다시 검사한다.
 */
export function SurveyPage(): ReactElement {
  const [draft, setDraft] = useState<SurveyDraft>(EMPTY_DRAFT);
  const [hasAttempted, setHasAttempted] = useState(false);
  const submitter = useSubmitSurvey();

  // 제출 여부를 화면이 따로 기억하지 않는다 — 서버가 받았다는 사실이 곧 제출이다.
  if (submitter.isSuccess) return <SurveySent wantsConsulting={draft.consulting === 'yes'} />;

  const problems = hasAttempted ? findProblems(draft) : [];
  const errorFor = (key: SurveyFieldKey): string | undefined =>
    problems.find((problem) => problem.key === key)?.message;

  const update = (patch: Partial<SurveyDraft>): void =>
    setDraft((current) => ({ ...current, ...patch }));
  const updateOther = (key: Exclude<SurveyChoiceKey, 'aiExperience'>, text: string): void =>
    setDraft((current) => ({ ...current, other: { ...current.other, [key]: text } }));

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    // 잠금을 버튼의 `disabled` 에 맡기지 않는다 — 문의하기와 같은 이유(포커스를 잃는다).
    if (submitter.isPending) {
      return;
    }
    const [firstProblem] = findProblems(draft);
    if (firstProblem !== undefined) {
      setHasAttempted(true);
      moveTo(firstProblem);
      return;
    }
    submitter.submit(toSubmission(draft, homepageOf(event.currentTarget)));
  };

  return (
    <div className="u-container flex max-w-[52rem] flex-col gap-8 pb-20">
      <header className="flex flex-col gap-3 pt-12 text-center">
        <span className="text-[12px] font-bold tracking-[0.08em] text-primary">AUTOP Research</span>
        <h1 className="text-balance text-[clamp(1.5rem,1.2rem+1.2vw,2rem)] font-bold tracking-[-0.042em] md:whitespace-nowrap">
          AI 업무 자동화, 어디서부터 시작해야 할까요?
        </h1>
        <p className="text-[14.5px] leading-[1.8] text-muted-foreground">
          AI와 업무 툴을 연결해 반복 업무를 줄이는 실전 자동화 강의를 준비 중입니다!
          <br />
          가장 불편한 업무와 배우고 싶은 자동화 사례를 들려주세요.
        </p>
        <p className="mx-auto rounded-md bg-secondary px-4 py-2.5 text-[13.5px]">
          🎁 참여하시면 &lsquo;AI 워크플로우 + GPT 지침 프롬프트&rsquo; 자료를 메일로 보내드립니다
          😊
        </p>
        <p className="text-[12.5px] text-muted-foreground">⏱ 약 2~3분</p>
      </header>

      <ProgressBar answered={countAnswered(draft)} />

      <form
        noValidate
        className="relative flex flex-col gap-16"
        aria-busy={submitter.isPending}
        onSubmit={handleSubmit}
      >
        <Honeypot id="survey-homepage" />

        {SURVEY_SECTIONS.map((section) => (
          <section key={section.title} className="flex flex-col gap-11">
            <h2 className="border-border border-b pb-2 text-[13px] font-bold text-muted-foreground">
              {section.title}
            </h2>
            {section.questions.map((question) => (
              <ChoiceQuestion
                key={question.key}
                question={question}
                value={draft[question.key]}
                other={question.key === 'aiExperience' ? undefined : draft.other[question.key]}
                error={errorFor(question.key)}
                onChange={(next) => update({ [question.key]: next })}
                onOtherChange={(text) => {
                  if (question.key !== 'aiExperience') updateOther(question.key, text);
                }}
              />
            ))}
          </section>
        ))}

        <section className="flex flex-col gap-11">
          <h2 className="border-border border-b pb-2 text-[13px] font-bold text-muted-foreground">
            강의 · 자료 받기
          </h2>

          <div id="survey-q-story" className="flex scroll-mt-36 flex-col gap-2">
            <QuestionTitle
              as="label"
              htmlFor="survey-story"
              number={7}
              title={
                '요즘 "이건 진짜 누가 대신 해줬으면.." 하는 업무가 있나요? 그 상황을 들려주세요.'
              }
              required
            />
            <p id="survey-story-hint" className={hintClass}>
              정답은 없어요 😊 들려주신 만큼 강의와 자료에 더 잘 담을 수 있어요. 떠오르는 대로
              편하게 적어주세요.
            </p>
            <textarea
              id="survey-story"
              rows={5}
              maxLength={SURVEY_STORY_MAX_LENGTH}
              value={draft.story}
              aria-invalid={errorFor('story') ? true : undefined}
              aria-describedby={describedBy(
                'survey-story-hint',
                errorFor('story') !== undefined && 'survey-story-error',
              )}
              onChange={(event) => update({ story: event.target.value })}
              className={`${inputClass} mt-2 py-3`}
            />
            <p className="text-right text-[12px] text-muted-foreground">
              <span data-numeric="">{draft.story.length}</span>자
            </p>
            <FieldError id="survey-story-error" message={errorFor('story')} />
          </div>

          <div id="survey-q-email" className="flex scroll-mt-36 flex-col gap-2">
            <QuestionTitle
              as="label"
              htmlFor="survey-email"
              number={8}
              title="자료를 받을 이메일을 입력해주세요!"
              required
            />
            {/* 한 이메일에 응답 하나다(서버가 막는다). 미리 알려 두지 않으면 다시 낸 사람이 거절을
                뜻밖의 오류로 읽는다. */}
            <p id="survey-email-hint" className={hintClass}>
              입력해주신 메일로 AI 워크플로우 자료 + GPT 지침 프롬프트를 보내드려요.
              <br />한 이메일로 한 번만 참여할 수 있어요.
            </p>
            <input
              id="survey-email"
              type="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={draft.email}
              aria-invalid={errorFor('email') ? true : undefined}
              aria-describedby={describedBy(
                'survey-email-hint',
                errorFor('email') !== undefined && 'survey-email-error',
              )}
              onChange={(event) => update({ email: event.target.value })}
              className={`${inputClass} mt-2 h-11`}
            />
            <FieldError id="survey-email-error" message={errorFor('email')} />
          </div>

          <fieldset
            id="survey-q-consulting"
            aria-describedby={describedBy(
              errorFor('consulting') !== undefined && 'survey-consulting-error',
            )}
            className="flex scroll-mt-36 flex-col gap-4"
          >
            <QuestionTitle
              as="legend"
              number={9}
              title="1:1 무료 컨설팅 추첨에 응모하시겠어요?"
              required
            />
            <p className={`${hintClass} -mt-2`}>
              🎁 추첨으로 5분께 1:1 무료 컨설팅 — 어디를 자동화하면 좋을지 함께 찾아드려요.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {SURVEY_CONSULTING_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-card px-3.5 py-3 text-[13.5px] has-checked:border-foreground has-checked:bg-secondary"
                >
                  <input
                    type="radio"
                    name="survey-consulting"
                    value={option.value}
                    checked={draft.consulting === option.value}
                    onChange={() => update({ consulting: option.value })}
                    className="size-4 shrink-0 accent-primary"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <FieldError id="survey-consulting-error" message={errorFor('consulting')} />
          </fieldset>

          <SurveyConsent
            agreedToPrivacy={draft.agreedToPrivacy}
            marketingOptIn={draft.marketingOptIn}
            error={errorFor('agreedToPrivacy')}
            onAgreedChange={(next) => update({ agreedToPrivacy: next })}
            onMarketingChange={(next) => update({ marketingOptIn: next })}
          />
        </section>

        <div className="flex flex-col gap-3">
          {problems.length > 0 ? (
            <p role="alert" className="text-[13px] text-destructive">
              빠진 문항이 {problems.length}개 있어요. 표시된 곳을 확인해 주세요.
            </p>
          ) : null}
          {/* 실패를 삼키지 않는다 — 조용히 넘기면 사용자는 제출된 줄 알고 메일을 기다린다. */}
          {submitter.error ? (
            <p role="alert" className="text-[13px] text-destructive">
              {toMessage(submitter.error)}
            </p>
          ) : null}
          <Button type="submit" size="lg" aria-disabled={submitter.isPending}>
            {submitter.isPending ? '보내는 중…' : '응답 제출하기 →'}
          </Button>
          <p className="text-center text-[12px] text-muted-foreground">* 표시는 필수 문항이에요</p>
        </div>
      </form>
    </div>
  );
}
